/**
 * What can be said about a strategy WITHOUT a single price — ROADMAP 5.19.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE CARD CAN CLAIM ANYTHING AT ALL ON A PAGE WITH NO FEED
 *
 * The Strategies page never subscribes to market data. It is configuration,
 * opened at midnight as readily as at 09:20, so every figure on a card has to
 * be derivable from the TEMPLATE alone — sides, option types, ratios and
 * strike rules. Anything needing a quote (max loss in rupees, breakevens, the
 * net credit) belongs on the payoff dock, where there IS a chain to read.
 *
 * That constraint is less limiting than it sounds, because the single most
 * important fact about an options structure — whether a tail is open — is a
 * property of the legs and not of their prices. See `riskShape`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { strikeCriteriaLabel, structureSideOf, type StructureSide } from '@apna/shared'
import type { StrategyDto, StrategyLegDto } from '@/types/strategy'

/**
 * Is the loss bounded at expiry, and if not, on which side?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SLOPE OF THE TAIL IS A COUNT, NOT A CALCULATION
 *
 * A payoff is piecewise linear with kinks only at strikes, so far enough
 * above EVERY call strike each call is in the money and the slope is simply
 * the net signed call units. If that is negative the loss grows for ever
 * upwards. Below every put strike the mirror holds, with the sign flipped
 * because a put gains as spot falls.
 *
 * The consequence worth stating: THE STRIKES DO NOT ENTER INTO IT. Only the
 * sides, the option types and the ratios do — which is why a leg whose strike
 * rule is unreadable still cannot hide an open tail from this function, and
 * why the answer holds for every underlying and every expiry at once.
 *
 *        P&L │                                  ╱  net CE units > 0
 *            │     ┌──────────────┐            ╱
 *         ───┼─────┴──────────────┴───────────╱──── spot
 *            │ ╲                               
 *            │  ╲  net PE units < 0 → open below
 *
 * A DEFINED-RISK STRUCTURE IS NOT A SAFE ONE. It says the worst case is a
 * finite number, not that the number is small; an Iron Condor sized at 40
 * lots can lose more than a one-lot naked call. The card says "Defined"
 * rather than "Low".
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type RiskShape = 'DEFINED' | 'OPEN_ABOVE' | 'OPEN_BELOW' | 'OPEN_BOTH'

/** Signed units of one option type. Positive is long. */
function netUnits(legs: readonly StrategyLegDto[], optionType: 'CE' | 'PE'): number {
  return legs
    .filter((l) => l.optionType === optionType)
    .reduce((n, l) => n + (l.side === 'BUY' ? l.ratio : -l.ratio), 0)
}

export function riskShape(legs: readonly StrategyLegDto[]): RiskShape {
  const above = netUnits(legs, 'CE') < 0
  const below = netUnits(legs, 'PE') < 0
  if (above && below) return 'OPEN_BOTH'
  if (above) return 'OPEN_ABOVE'
  if (below) return 'OPEN_BELOW'
  return 'DEFINED'
}

/**
 * Does the structure COLLECT premium or PAY it?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROVED FROM THE ORDERING OF PREMIUMS, NEVER FROM THEIR VALUES
 *
 * This is the question a trader asks straight after "where are the tails",
 * and it looks like it needs a chain: the net premium is a sum of rupee
 * amounts nobody has fetched. It does not, because for a great many
 * structures the SIGN of that sum is fixed by the ordering alone.
 *
 * Two facts about option prices do the work, and both hold on every chain
 * that is free of arbitrage:
 *
 *   A call is worth less the higher its strike.   p(K) strictly decreasing
 *   A put  is worth less the lower  its strike.   p(K) strictly increasing
 *
 * Order one option type so its premiums descend — calls by ascending strike,
 * puts by descending strike — and write the net cash paid as
 *
 *      Σ cᵢ pᵢ  =  Σ Sᵢ (pᵢ − pᵢ₊₁),      Sᵢ = c₁ + … + cᵢ,  p_{n+1} = 0
 *
 * which is summation by parts. Every (pᵢ − pᵢ₊₁) is positive, so if every
 * RUNNING TOTAL Sᵢ has the same sign, the whole sum has that sign — whatever
 * the prices happen to be that morning. Positive means we paid: a DEBIT.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND IT REFUSES WHEN THE ANSWER GENUINELY DEPENDS ON THE PRICES
 *
 * A Call Ratio Spread buys one call and sells two further out. Its running
 * totals are +1 then −1, so the test declines, and declining is correct:
 * whether two cheap calls outsell one dear one is a fact about today's skew,
 * not about the structure. Same for a Broken Wing Butterfly, which is a
 * credit at one wing width and a debit at another — the very thing that
 * distinguishes it from a plain fly.
 *
 * A card that guessed "Credit" there would be wrong on the exact structures
 * where the answer is worth money.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export type PremiumFlow = 'CREDIT' | 'DEBIT' | 'DEPENDS'

/** `null` when this option type is absent and so has no opinion. */
function sideFlow(
  legs: readonly StrategyLegDto[],
  optionType: 'CE' | 'PE',
): PremiumFlow | null {
  const side = legs.filter((l) => l.optionType === optionType)
  if (side.length === 0) return null

  /*
   * ALL ONE WAY NEEDS NO ORDERING, which is what keeps a premium-selected
   * leg answerable. "Sell a call and a put at about ₹120" is a credit
   * whatever strikes it lands on, and the sorting below could not have told
   * us so — a CLOSEST_PREMIUM leg has no strike until the chain is read.
   */
  if (side.every((l) => l.side === 'SELL')) return 'CREDIT'
  if (side.every((l) => l.side === 'BUY')) return 'DEBIT'

  // Mixed sides: the proof needs the strike ladder, so every leg must be on it.
  const rungs = new Map<number, number>()
  for (const l of side) {
    if (l.strikeCriteria?.kind !== 'ATM_OFFSET') return 'DEPENDS'
    const units = l.side === 'BUY' ? l.ratio : -l.ratio
    /*
     * ONE RUNG PER STRIKE, not one per leg. Two legs at the same strike
     * share a price, so summation by parts sees a single term — and
     * splitting them would make the running totals depend on the order the
     * rows came back in. A long and a short of equal size at one strike
     * would read as a debit or a credit purely by which was stored first.
     */
    rungs.set(l.strikeCriteria.steps, (rungs.get(l.strikeCriteria.steps) ?? 0) + units)
  }

  // Descending premium: calls up the ladder, puts down it.
  const ladder = [...rungs.entries()].sort((a, b) =>
    optionType === 'CE' ? a[0] - b[0] : b[0] - a[0],
  )

  let running = 0
  let sawPositive = false
  let sawNegative = false
  for (const [, units] of ladder) {
    running += units
    if (running > 0) sawPositive = true
    if (running < 0) sawNegative = true
    if (sawPositive && sawNegative) return 'DEPENDS'
  }
  if (sawPositive) return 'DEBIT'
  if (sawNegative) return 'CREDIT'
  /*
   * Every running total was zero, so the legs cancel rung for rung — a
   * long and a short of equal size at the same strike. Nothing is paid.
   */
  return 'DEPENDS'
}

export function premiumFlow(legs: readonly StrategyLegDto[]): PremiumFlow {
  const call = sideFlow(legs, 'CE')
  const put = sideFlow(legs, 'PE')
  if (call === null) return put ?? 'DEPENDS'
  if (put === null) return call
  /*
   * One side collecting while the other pays is NOT answerable. Comparing
   * the two would mean comparing magnitudes across option types, and
   * nothing above licenses that — only the ordering within one type.
   */
  return call === put ? call : 'DEPENDS'
}

/**
 * "ATM" / "OTM 2" / "₹120" — the rule in the shortest honest form.
 *
 * The spelling itself lives in `@apna/shared`, so a leg reads identically on
 * a card, in the builder and in a deployment record. Two spellings of one
 * rule is how a trader comes to believe they are two rules. This wrapper
 * only adds the case the shared writer cannot have an opinion about: a
 * stored rule that does not parse at all.
 */
export function criteriaLabel(leg: StrategyLegDto): string {
  if (!leg.strikeCriteria) return 'unreadable'
  return strikeCriteriaLabel(leg.strikeCriteria, leg.optionType)
}

/** "S CE ATM+2 ×2" — one leg, as the chips and the search both read it. */
export function legLabel(leg: StrategyLegDto): string {
  const base = `${leg.side === 'BUY' ? 'B' : 'S'} ${leg.optionType} ${criteriaLabel(leg)}`
  return leg.ratio > 1 ? `${base} ×${leg.ratio}` : base
}

/** Everything the card renders that is not already a column on the DTO. */
export interface StrategyFacts {
  risk: RiskShape
  /** Which form the legs are written in — see `structureSideOf`. */
  structure: StructureSide
  /** Does it collect premium or pay it, when that is provable at all. */
  premium: PremiumFlow
  /** Total contracts per lot, both sides summed. A size, not a net. */
  units: number
  /** True when any leg's stored rule does not parse. */
  unreadable: boolean
}

export function strategyFacts(s: StrategyDto): StrategyFacts {
  return {
    risk: riskShape(s.legs),
    structure: structureSideOf(s.legs),
    premium: premiumFlow(s.legs),
    units: s.legs.reduce((n, l) => n + l.ratio, 0),
    unreadable: s.legs.some((l) => l.strikeCriteria === null),
  }
}
